import { lazy } from "react";
import { Navigate, type RouteObject } from "react-router-dom";
import { AuthedLayout, Layout } from "../components/Layout";
import { ConfirmAction } from "./Auth/ConfirmAction";
import { ErrorPage } from "./ErrorPage";

const Account = lazy(() =>
  import("./Account").then((module) => ({ default: module.Account })),
);

const Login = lazy(() =>
  import("./Auth").then((module) => ({ default: module.Login })),
);
const Signup = lazy(() =>
  import("./Auth").then((module) => ({ default: module.Signup })),
);
const ResetPassword = lazy(() =>
  import("./Auth").then((module) => ({ default: module.ResetPassword })),
);
const GuestSignup = lazy(() =>
  import("./Auth").then((module) => ({ default: module.GuestSignup })),
);
const ActivityRoute = lazy(() =>
  import("./Activity").then((module) => ({ default: module.ActivityPage })),
);
const Build = lazy(() =>
  import("./Build").then((module) => ({ default: module.Build })),
);
const OnboardingPage = lazy(() =>
  import("./Onboarding").then((module) => ({ default: module.OnboardingPage })),
);
const GuestOnboardingPage = lazy(() =>
  import("./Onboarding/GuestOnboarding").then((module) => ({
    default: module.GuestOnboarding,
  })),
);
const BlockPage = lazy(() =>
  import("./Build/BlockPage").then((module) => ({ default: module.BlockPage })),
);

const Create = lazy(() =>
  import("./Create").then((module) => ({ default: module.Create })),
);
const Asset = lazy(() =>
  import("./Create/Asset").then((module) => ({ default: module.AssetPage })),
);
const IdeasPage = lazy(() =>
  import("./Ideas").then((module) => ({ default: module.IdeasPage })),
);
const Subscribe = lazy(() =>
  import("./Subscribe").then((module) => ({ default: module.Subscribe })),
);

const DocumentsPage = lazy(() =>
  import("./Documents").then((module) => ({
    default: module.DocumentsPage,
  })),
);

const IntegrationPage = lazy(() =>
  import("./Integration").then((module) => ({
    default: module.IntegrationPage,
  })),
);

const ContactsPage = lazy(() =>
  import("./Contacts").then((module) => ({
    default: module.ContactsPage,
  })),
);
const ContactPage = lazy(() =>
  import("./Contacts/ContactPage").then((module) => ({
    default: module.ContactPage,
  })),
);
const WorkspacePage = lazy(() =>
  import("./Workspace").then((module) => ({
    default: module.WorkspacePage,
  })),
);

const PermissionLevelsPage = lazy(() =>
  import("./PermissionLevels").then((module) => ({
    default: module.PermissionLevelsPage,
  })),
);

const TasksPage = lazy(() =>
  import("./Tasks").then((module) => ({
    default: module.TasksPage,
  })),
);

const FactInsightsPage = lazy(() =>
  import("./FactInsights").then((module) => ({
    default: module.FactInsightsPage,
  })),
);

const ProjectGraphPage = lazy(() =>
  import("./ProjectGraph").then((module) => ({
    default: module.ProjectGraphPage,
  })),
);

const ClaimProjectPage = lazy(() =>
  import("./ClaimProject").then((module) => ({
    default: module.ClaimProjectPage,
  })),
);

const AgentsPage = lazy(() =>
  import("./Agents").then((module) => ({
    default: module.AgentsPage,
  })),
);

const OAuthConsentPage = lazy(() =>
  import("./OAuthConsent").then((module) => ({
    default: module.OAuthConsentPage,
  })),
);

export const routeDefinitions: RouteObject[] = [
  {
    // Optional authed routes without navigation
    element: <Layout withPadding={false} />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: "/login",
        element: <Login />,
      },
      {
        path: "/join",
        element: <Signup />,
      },
      {
        path: "/guest/join",
        element: <GuestSignup />,
      },
      {
        path: "/confirm",
        element: <ConfirmAction />,
      },
      {
        path: "/roles-permissions",
        element: <PermissionLevelsPage />,
      },
      {
        path: "/404",
        element: <ErrorPage code={404} />,
      },
    ],
  },
  {
    // Required authed routes with default navigation
    element: <AuthedLayout variant="sidebar" />,
    errorElement: <ErrorPage />,
    children: [
      {
        errorElement: <ErrorPage />,
        children: [
          {
            path: "/",
            element: <Navigate to="/activity" />,
          },
          {
            path: "/activity",
            element: <ActivityRoute />,
          },
          {
            path: "/subscribe",
            element: <Subscribe />,
          },
          {
            path: "/account/:section?/:subSection?",
            element: <Account />,
          },
          {
            path: "/strategy",
            element: <Navigate to="/build" />,
          },
          {
            path: "/build",
            element: <Build />,
          },
          {
            path: "/build/stacks",
            element: <Navigate to="/build" />,
          },
          {
            path: "/build/b/:blockPath",
            element: <BlockPage />,
          },
          {
            path: "/create",
            element: <Create />,
          },
          {
            path: "/create/a/:assetId/:assetVersionId?",
            element: <Asset />,
          },
          {
            path: "/ideas",
            element: <IdeasPage />,
          },
          {
            path: "/tasks",
            element: <TasksPage />,
          },

          /**
           * Links to workspace threads continue to be valid; /thread/ is added also as
           * a more generic alias away from the workspace naming.
           */
          {
            path: "/workspace/:chatThreadId?",
            element: <WorkspacePage />,
          },
          {
            path: "/thread/:chatThreadId?",
            element: <WorkspacePage />,
          },
          {
            path: "/network",
            children: [
              {
                path: "",
                element: <ContactsPage />,
              },
              {
                path: "/network/c/:contactId",
                element: <ContactPage />,
              },
            ],
          },
          {
            path: "/docs",
            element: <DocumentsPage />,
          },
          {
            path: "/agents",
            element: <AgentsPage />,
          },
          {
            path: "/apps/:appId",
            element: <IntegrationPage />,
          },
          {
            path: "/insights",
            element: <FactInsightsPage />,
          },
          {
            path: "/project-graph",
            element: <ProjectGraphPage />,
          },
          {
            path: "/claim-project",
            element: <ClaimProjectPage />,
          },
          {
            path: "*",
            element: <ErrorPage code={404} />,
          },
        ],
      },
    ],
  },
  {
    element: <AuthedLayout variant="minimal" />,
    errorElement: <ErrorPage />,
    children: [
      {
        errorElement: <ErrorPage />,
        children: [
          {
            path: "/reset-password",
            element: <ResetPassword isResetFlow />,
          },
          {
            path: "/welcome",
            element: <OnboardingPage />,
          },
          {
            path: "/guest/welcome",
            element: <GuestOnboardingPage />,
          },
          {
            path: "/oauth/consent",
            element: <OAuthConsentPage />,
          },
        ],
      },
    ],
  },
];
